import { FastTrackPanel } from "@/components/molecules/fast-track";
import { MembersSelect } from "@/components/molecules/members-select";
import { ScheduleSelect } from "@/components/molecules/schedule-select";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  CardTitle as DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PriceInput } from "@/components/ui/price-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  useCreateService,
  useUpdateService,
} from "@/features/services/hooks/use-services";
import type { Service } from "@/features/services/types";
import { useTranslation } from "@/features/utils/i18n";
import { PlusIcon } from "@phosphor-icons/react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

interface ServiceDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  service?: Service;
}

export const ServiceDialog = ({
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  service,
}: ServiceDialogProps) => {
  const { t } = useTranslation("services");
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const onOpenChange = controlledOnOpenChange ?? setUncontrolledOpen;

  const [name, setName] = useState(service?.name || "");
  const [description, setDescription] = useState(service?.description || "");
  const [duration, setDuration] = useState(
    service?.duration?.toString() || "60",
  );
  const [price, setPrice] = useState(service?.price || 0);
  const [scheduleId, setScheduleId] = useState(service?.schedule_id || "");
  const [performerIds, setPerformerIds] = useState<string[]>(
    service?.performers?.ids || [],
  );

  // Update form when service prop changes (for edit mode)
  useEffect(() => {
    if (service) {
      setName(service.name);
      setDescription(service.description);
      setDuration(service.duration?.toString() || "60");
      setPrice(service.price);
      setScheduleId(service.schedule_id || "");
      setPerformerIds(service.performers?.ids || []);
    }
  }, [service]);

  const createService = useCreateService();
  const updateService = useUpdateService();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const data = {
      name,
      description,
      duration: parseInt(duration),
      price,
      schedule_id: scheduleId,
      performers: { ids: performerIds },
    };

    try {
      if (service?.id) {
        await updateService.mutateAsync({
          serviceId: service.id,
          data,
        });
        toast.success(t("messages.updateSuccess"));
      } else {
        await createService.mutateAsync(data);
        toast.success(t("messages.createSuccess"));
      }
      onOpenChange(false);
      resetForm();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : service
            ? t("messages.updateError")
            : t("messages.createError"),
      );
    }
  };

  const resetForm = () => {
    if (!service) {
      setName("");
      setDescription("");
      setDuration("60");
      setPrice(0);
      setScheduleId("");
      setPerformerIds([]);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {!service && (
        <DialogTrigger asChild>
          <Button>
            <PlusIcon className="h-4 w-4 mr-2" />
            {t("addService")}
          </Button>
        </DialogTrigger>
      )}
      <DialogContent className="max-w-2xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {service?.id ? t("dialog.editTitle") : t("dialog.createTitle")}
            </DialogTitle>
            <DialogDescription>
              {service?.id
                ? t("dialog.editDescription")
                : t("dialog.createDescription")}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Only on creation: an edit is the client correcting one row by
                hand, and a button that rewrites the whole catalogue has no
                business sitting on top of that form. */}
            {!service?.id && (
              <FastTrackPanel
                scope="services"
                onLeave={() => onOpenChange(false)}
              />
            )}

            {/* A synced service is only partly the provider's: what the client
                fills in here is theirs and the sync stops rewriting it. The
                rule needs saying, because handing a field back is done by
                clearing it and nothing on screen would suggest that. */}
            {service?.id && service.adapter && (
              <div className="rounded-md border border-dashed bg-muted/40 p-3 text-sm text-muted-foreground">
                {t("dialog.externalNotice", {
                  adapter: service.adapter.adapter,
                })}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="name">{t("dialog.serviceName")}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t("dialog.serviceNamePlaceholder")}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">{t("dialog.description")}</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("dialog.descriptionPlaceholder")}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="performers">{t("dialog.performers")}</Label>
              <MembersSelect
                value={performerIds}
                onChange={setPerformerIds}
                multiple
                allowAll
                placeholder={t("dialog.performersPlaceholder")}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="schedule">{t("dialog.schedule")}</Label>
              <ScheduleSelect
                value={scheduleId}
                onChange={setScheduleId}
                allowNone
                placeholder={t("dialog.schedulePlaceholder")}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="duration">{t("dialog.duration")}</Label>
                <Select value={duration} onValueChange={setDuration}>
                  <SelectTrigger id="duration">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: 32 }, (_, i) => (i + 1) * 15).map(
                      (mins) => (
                        <SelectItem key={mins} value={mins.toString()}>
                          {mins < 60
                            ? `${mins} min`
                            : mins === 60
                              ? "1 hour"
                              : `${Math.floor(mins / 60)}h ${mins % 60 > 0 ? `${mins % 60}m` : ""}`.trim()}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="price">{t("dialog.price")}</Label>
                <PriceInput
                  id="price"
                  value={price}
                  onChange={setPrice}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onOpenChange(false);
                resetForm();
              }}
            >
              {t("dialog.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={createService.isPending || updateService.isPending}
            >
              {createService.isPending || updateService.isPending
                ? t("dialog.saving")
                : service?.id
                  ? t("dialog.update")
                  : t("dialog.create")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
